import { useEffect, useMemo } from "react";
import {
  Autocomplete,
  TextField,
  Stack,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Divider,
  Checkbox,
} from "@mui/material";
import {
  TemplateOptionSerialised,
  StructureSetEntry,
  DensityOverrideSerialised,
} from "@providers/hop-ord-server/api";
import LoadingRTStructTPS from "@pages/OrderTracker/components/PlanningAutomation/LoadingRTStructTPS";
import DensityOverride, { DensityOverrideMapping } from "./DensityOverride";

const CARD_HEADER_SUBTITLE =
  "Structures marked with * are mandatory. Unmapped template structure objectives will be removed for planning automation upon submission. ";

interface Props {
  hasSubmitted: boolean;
  setHasSubmitted: (hasSubmitted: boolean) => void;
  template?: TemplateOptionSerialised;
  availableStructures?: StructureSetEntry[] | null;
  availableMaterials?: DensityOverrideSerialised[] | null;
  setStructureState: (structures: StructureMap[]) => void;
  structureState: StructureMap[];
  runValidation: (structures: StructureMap[]) => boolean;
  densityOverrideState: DensityOverrideMapping[];
  setDensityOverrideState: (overrides: DensityOverrideMapping[]) => void;
  showDensityOverrideError?: boolean;
}

export interface StructureMap {
  id: number;
  name: string;
  mandatory: boolean;
  values: string[];
  isValid: boolean;
}

export type { DensityOverrideMapping };

const StructureMapping = ({
  availableStructures,
  availableMaterials,
  template,
  setHasSubmitted,
  setStructureState,
  structureState,
  runValidation,
  densityOverrideState,
  setDensityOverrideState,
  showDensityOverrideError,
}: Props): JSX.Element => {
  const structureOptions = useMemo(
    () => availableStructures?.map((structure) => structure.name),
    [availableStructures],
  );

  /*
  Each template has a set of structures that the user must map to the available patient structures
  from the TPS provided by automation.
  When a template is selected, loop through the each template structure and use it's provided
  regex to automatically map patient structures to template structures.
  */
  useEffect(() => {
    // reset state on template change
    setHasSubmitted(false);

    const templateMap: StructureMap[] = [];
    template?.data?.forEach((t, idx) => {
      const regexes = typeof t.regex === "string" ? [t.regex] : t.regex;
      let initialMappedStructs: string[] = [];

      for (const regex of regexes ?? []) {
        // Only use the first regex that matches
        const mappedStructs = availableStructures
          ?.filter((option) => {
            return new RegExp(regex, "gi").test(option?.name);
          })
          .map((option) => option.name);

        if (mappedStructs && mappedStructs.length > 0) {
          initialMappedStructs = mappedStructs;
          break;
        }
      }

      templateMap.push({
        id: idx,
        name: t.name,
        mandatory: t.mandatory,
        values: initialMappedStructs,
        isValid: true,
      });
    });

    setStructureState(templateMap);
  }, [template, availableStructures]);

  const handleMappingChange = (id: number, newValue: string[]) => {
    const newStructures = structureState.map((structs) => {
      return structs.id === id
        ? {
            ...structs,
            values: newValue,
            isValid: !(!newValue.length && structs.mandatory),
          }
        : structs;
    });
    setStructureState(newStructures);
    runValidation(newStructures);
  };

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <Stack
        sx={{
          maxHeight: 1,
          height: 1,
          width: 1,
        }}
      >
        <Card variant="outlined">
          <CardHeader
            title="Map structures"
            subheader={CARD_HEADER_SUBTITLE}
            slotProps={{
              subheader: { typography: "body2" },
              title: { typography: "subtitle1" },
            }}
          />
          <CardContent sx={{ "&:last-child": { paddingBottom: 2 } }}>
            {availableStructures ? (
              <Stack
                sx={{
                  gap: 2,
                }}
              >
                <Stack direction="row">
                  <Typography variant="subtitle2" sx={{ width: "100%" }}>
                    Template structure
                  </Typography>
                  <Typography variant="subtitle2" sx={{ width: "100%" }}>
                    Available structure
                  </Typography>
                </Stack>
                <Divider />
                <Stack
                  data-testid="structure-table"
                  sx={{
                    gap: 2,
                  }}
                >
                  {structureState?.map((ts) => {
                    return (
                      <Stack
                        direction="row"
                        key={ts.id}
                        sx={{
                          gap: 1,
                          mr: 6,
                        }}
                      >
                        <Typography
                          data-testid={`planning-template-key-${ts.id}`}
                          variant="body2"
                          sx={{
                            lineHeight: 3,
                            width: "100%",
                          }}
                        >
                          {ts?.name} {ts?.mandatory && "*"}
                        </Typography>
                        <Autocomplete
                          id={`planning-template-value-${ts.id}`}
                          data-testid={`planning-template-value-${ts.id}`}
                          options={structureOptions || []}
                          fullWidth
                          size="small"
                          multiple
                          renderInput={(params) => (
                            <TextField
                              error={!ts.isValid}
                              helperText={
                                !ts.isValid && "This field is required"
                              }
                              {...params}
                              label=""
                            />
                          )}
                          value={ts.values}
                          defaultValue={[]}
                          renderOption={(props, option, { selected }) => (
                            <li {...props}>
                              <Checkbox checked={selected} />
                              {option}
                            </li>
                          )}
                          onChange={(_, mappedValue) => {
                            handleMappingChange(ts.id, mappedValue);
                          }}
                        />
                      </Stack>
                    );
                  })}
                </Stack>
              </Stack>
            ) : (
              <LoadingRTStructTPS />
            )}
            {availableStructures && (availableMaterials?.length ?? 0) > 0 && (
              <DensityOverride
                availableStructures={availableStructures}
                availableMaterials={availableMaterials!}
                densityOverrideState={densityOverrideState}
                setDensityOverrideState={setDensityOverrideState}
                showDensityOverrideError={showDensityOverrideError}
              />
            )}
          </CardContent>
        </Card>
      </Stack>
    </Stack>
  );
};

export default StructureMapping;
